import { Box, Paper, Typography, LinearProgress, Stack, Alert, Chip } from '@mui/material'
import {
  CheckCircle as CheckIcon,
  Schedule as QueuedIcon,
  Autorenew as ProcessingIcon,
  Error as ErrorIcon,
} from '@mui/icons-material'
import type { ProcessingJob } from '@/types/invoiceAnalyser'

interface ProcessingStatusProps {
  job: ProcessingJob
}

const STEP_LABELS: Record<number, string> = {
  0: 'Initializing...',
  10: 'Loading document...',
  20: 'Analyzing document type...',
  30: 'Preparing for processing...',
  50: 'Extracting data...',
  70: 'Validating extraction...',
  90: 'Finalizing results...',
  100: 'Complete!',
}

export function ProcessingStatus({ job }: ProcessingStatusProps) {
  const getStatusIcon = () => {
    switch (job.status) {
      case 'queued':
        return <QueuedIcon sx={{ fontSize: 48, color: 'warning.main' }} />
      case 'processing':
        return <ProcessingIcon
          sx={{ fontSize: 48, color: 'primary.main', animation: 'spin 1s linear infinite' }}
        />
      case 'completed':
        return <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
      case 'failed':
        return <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
    }
  }

  const getStatusColor = () => {
    switch (job.status) {
      case 'queued':
        return 'warning'
      case 'processing':
        return 'info'
      case 'completed':
        return 'success'
      case 'failed':
        return 'error'
    }
  }

  const getStatusLabel = () => {
    switch (job.status) {
      case 'queued':
        return 'Queued for processing'
      case 'processing':
        return 'Processing...'
      case 'completed':
        return 'Processing complete!'
      case 'failed':
        return 'Processing failed'
    }
  }

  const getProgressLabel = () => {
    if (job.progressMessage) {
      return job.progressMessage
    }
    return STEP_LABELS[Math.floor(job.progress / 10) * 10] || 'Processing...'
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        {getStatusIcon()}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" gutterBottom>
            {getStatusLabel()}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {getProgressLabel()}
          </Typography>
        </Box>
        <Chip
          label={`${Math.round(job.progress)}%`}
          color={getStatusColor() as any}
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      {job.status === 'processing' && (
        <LinearProgress
          variant="determinate"
          value={job.progress}
          sx={{ height: 8, borderRadius: 4, mb: 2 }}
        />
      )}

      {job.status === 'processing' && (
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="textSecondary">
              Progress
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {Math.round(job.progress)}%
            </Typography>
          </Box>
        </Stack>
      )}

      {job.status === 'failed' && job.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {job.error}
        </Alert>
      )}

      {job.status === 'completed' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Invoice data extracted successfully! View the results below.
        </Alert>
      )}
    </Paper>
  )
}
